<!--@format-->
<template>
    <div class="journey-task-step-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Task</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="form-row">
                        <app-input v-model="step.title" label="Title *" placeholder=" " />
                    </div>
                    <div class="form-row">
                        <label>Task Type *</label>
                        <task-type-search v-model="step.taskType" />
                    </div>
                    <div v-if="clientId" class="form-row">
                        <label>Assign To</label>
                        <user-assign-dropdown
                            v-model="step.assignTo"
                            :clientId="clientId"
                            :initialId="step.initialAssignedToId"
                            salesRoundRobin
                        />
                    </div>
                    <div class="due-date-row form-row">
                        <label>When is it due?</label>
                        <div class="input-fields">
                            <app-input v-model="step.dueAfterDuration" type="number" />
                            <duration-dropdown v-model="step.dueAfterDurationType" />
                            <span>after creation.</span>
                        </div>
                    </div>
                    <div class="form-row">
                        <label>Description</label>
                        <app-text-editor
                            v-model="step.description"
                            :config="config"
                            :showBrandingColors="false"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save" :disabled="loading" :loading="loading"
                        >Save</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const DurationDropdown = () => import('@/components/common/duration-dropdown');
const AppInput = () => import('@/components/common/app-input');
const AppTextEditor = () => import('@/components/common/app-text-editor');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const TaskTypeSearch = () => import('@/components/tasks/task-type-search');
const UserAssignDropdown = () => import('@/components/user/user-assign-dropdown');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'journey-task-step-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-text-editor': AppTextEditor,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'duration-dropdown': DurationDropdown,
        'task-type-search': TaskTypeSearch,
        'user-assign-dropdown': UserAssignDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,

            clientId: null,

            step: {
                id: this.widget.stepId,
                title: null,
                taskType: null,
                initialAssignedToId: null,
                assignTo: null,
                dueAfterDuration: null,
                dueAfterDurationType: null,
                description: null,
                templateId: this.widget.journeyTemplateId,
            },

            config: {
                key: this.$helpers.getFroalaKey(),
                toolbarButtons: [
                    'paragraphFormat',
                    'fontFamily',
                    'fontSize',
                    'align',
                    'bold',
                    'italic',
                    'colorPicker',
                    '|',
                    'formatOL',
                    'formatUL',
                    'insertImage',
                    'insertImage',
                    'insertLink',
                    'insertTable',
                    'undo',
                    'redo',
                ],
                fontFamilySelection: true,
                fontSizeSelection: true,
                paragraphFormatSelection: true,
                fontFamily: this.$helpers.minimalFontStyles,

                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: `https://api.honeycrm.com/api/Files/Froala`,
                fileUploadURL: `https://api.honeycrm.com/api/Files/Froala`,

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],

                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },
            },
        };
    },

    created() {
        this.getStep();
    },

    methods: {
        save() {
            this.loading = true;

            this.step.dueAfterDuration = parseInt(this.step.dueAfterDuration);
            this.patchStep();
        },

        patchStep() {
            var changes = [
                {
                    name: 'Name',
                    value: this.step.title,
                },
                {
                    name: 'TaskAssignedTo',
                    value: this.step.assignTo ? this.step.assignTo.id : null,
                },
                {
                    name: 'TaskType',
                    value: this.step.taskType,
                },
                {
                    name: 'TaskDueAfterDuration',
                    value: this.step.dueAfterDuration,
                },
                {
                    name: 'TaskDueAfterDurationType',
                    value: this.step.dueAfterDurationType,
                },
                {
                    name: 'TaskDescription',
                    value: this.step.description,
                },
            ];

            this.$api
                .patch(`/api/JourneySteps/${this.step.id}`, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification('Step successfully saved.');
                    EventBus.$emit(`journey-step:update`, this.step);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your step.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getStep() {
            var expandParam = `$expand=Journey($select=ClientId),JourneyTemplate($select=ClientId)`;
            var filterParam = `$filter=Id eq ${this.step.id}`;
            var selectParam = `$select=Name,TaskType,TaskDueAfterDuration,TaskDueAfterDurationType,TaskDescription,TaskAssignedTo, Journey`;
            var restUrl = `/service/JourneySteps?${expandParam}&${filterParam}&${selectParam}`;

            this.$api
                .get(restUrl)
                .then((response) => {
                    var responseValue = response.data.value[0];

                    this.clientId = responseValue.JourneyTemplate
                        ? responseValue.JourneyTemplate.ClientId
                        : responseValue.Journey.ClientId;

                    this.step.title = responseValue.Name;
                    this.step.initialAssignedToId = responseValue.TaskAssignedTo;
                    this.step.dueAfterDuration = responseValue.TaskDueAfterDuration;
                    this.step.dueAfterDurationType = responseValue.TaskDueAfterDurationType;
                    this.step.taskType = responseValue.TaskType;
                    this.step.description = responseValue.TaskDescription;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your step.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.journey-task-step-widget.widget .widget-contents-container .widget-header {
    background: #ff5a6f;
    background: transparent linear-gradient(85deg, #ff5a6f 0%, #f2cf80 100%);
}

.journey-task-step-widget .content-wrapper {
    padding: 0px;
}

.journey-task-step-widget .content-wrapper label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 7px;
}

.journey-task-step-widget .content-wrapper .form-row {
    margin-bottom: 20px;
}

.journey-task-step-widget .content-wrapper .input-fields {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.journey-task-step-widget .content-wrapper .input-fields .app-input {
    margin-right: 20px;
}

.journey-task-step-widget .due-date-row .input-fields > span {
    margin-left: 20px;
    width: 200px;
}

.journey-task-step-widget.widget .app-text-editor .fr-box.fr-basic.fr-top .fr-wrapper {
    border: 1px solid #e9e9ef;
}

.journey-task-step-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.journey-task-step-widget .cancel-btn:hover {
    background: #877d88;
}
.journey-task-step-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
